import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, ZoomIn, ZoomOut, RotateCw, Download, Maximize2 } from 'lucide-react';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { filesApi } from '../../services/api';

const ANIMATION_DURATION = 150;

const FilePreview = ({ file, onClose }) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [imageScale, setImageScale] = useState(1);
    const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [stlLoading, setStlLoading] = useState(false);
    const [stlError, setStlError] = useState(null);
    const [imageBlobUrl, setImageBlobUrl] = useState(null);
    const [imageLoading, setImageLoading] = useState(false);
    const [imageError, setImageError] = useState(null);
    const [isClosing, setIsClosing] = useState(false);

    const containerRef = useRef();
    const canvasRef = useRef();
    const sceneRef = useRef();
    const rendererRef = useRef();
    const cameraRef = useRef();
    const controlsRef = useRef();
    const meshRef = useRef();
    const animationIdRef = useRef();
    const loadingRef = useRef(false);

    const isImage = file.type === 'image' || /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(file.name);
    const isSTL = file.type === 'stl' || /\.stl$/i.test(file.name);

    // Génération d'URL pour les fichiers
    const getFileUrl = (file) => {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
        
        if (file.url) {
            // Utilise l'URL du fichier si elle est fournie (format: /api/files/:id)
            return file.url.startsWith('http') ? file.url : `${API_BASE_URL.replace('/api', '')}${file.url}`;
        } else if (file.id) {
            // Construit l'URL à partir de l'ID
            return `${API_BASE_URL.replace('/api', '')}/api/files/${file.id}`;
        } else if (isImage) {
            // Fallback pour la démo avec images placeholder
            return `https://picsum.photos/800/600?random=${file.name}`;
        }
        return null;
    };

    const initThreeJS = useCallback(() => {
        if (!canvasRef.current) return;

        setStlLoading(true);
        
        // Scene
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x404040);
        sceneRef.current = scene;

        // Camera standard
        const camera = new THREE.PerspectiveCamera(
            75,
            canvasRef.current.clientWidth / canvasRef.current.clientHeight,
            0.1,
            1000
        );
        camera.position.set(0, 0, 6);
        camera.lookAt(0, 0, 0);
        cameraRef.current = camera;

        // Renderer
        const renderer = new THREE.WebGLRenderer({ 
            canvas: canvasRef.current,
            antialias: true 
        });
        renderer.setSize(canvasRef.current.clientWidth, canvasRef.current.clientHeight);
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        rendererRef.current = renderer;

        // Contrôles : rotation (drag gauche), pan (drag droit ou shift+drag), zoom (molette)
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        controls.rotateSpeed = 0.9;
        controls.panSpeed = 0.8;
        controls.zoomSpeed = 0.9;
        controls.screenSpacePanning = true;
        controls.minDistance = 0.5;
        controls.maxDistance = 200;
        controlsRef.current = controls;

        // Éclairage : ambiante forte + hémisphère (ciel/sol) + key/fill/back lights
        // pour éclairer toutes les faces sans laisser de côté dans l'ombre.
        scene.add(new THREE.AmbientLight(0xffffff, 0.8));
        scene.add(new THREE.HemisphereLight(0xffffff, 0x444444, 0.6));

        const keyLight = new THREE.DirectionalLight(0xffffff, 0.7);
        keyLight.position.set(5, 8, 7);
        scene.add(keyLight);

        const fillLight = new THREE.DirectionalLight(0xffffff, 0.5);
        fillLight.position.set(-7, 3, -2);
        scene.add(fillLight);

        const backLight = new THREE.DirectionalLight(0xffffff, 0.4);
        backLight.position.set(0, -5, -8);
        scene.add(backLight);

        // Charger le fichier STL
        loadSTLFile();

        // Animation
        animate();
    }, []);

    // Initialisation de Three.js pour les fichiers STL
    useEffect(() => {
        if (isSTL && canvasRef.current) {
            // Nettoyer complètement avant d'initialiser
            if (animationIdRef.current) {
                cancelAnimationFrame(animationIdRef.current);
                animationIdRef.current = null;
            }
            if (controlsRef.current) {
                controlsRef.current.dispose();
                controlsRef.current = null;
            }
            if (rendererRef.current) {
                rendererRef.current.dispose();
                rendererRef.current = null;
            }
            sceneRef.current = null;
            cameraRef.current = null;
            meshRef.current = null;

            initThreeJS();
            return () => {
                if (animationIdRef.current) {
                    cancelAnimationFrame(animationIdRef.current);
                }
                if (controlsRef.current) {
                    controlsRef.current.dispose();
                }
                if (rendererRef.current) {
                    rendererRef.current.dispose();
                }
            };
        }
    }, [isSTL, initThreeJS]);

    // Charger l'image via l'API avec authentification
    useEffect(() => {
        if (isImage && file.id) {
            setImageLoading(true);
            setImageError(null);

            const loadImage = async () => {
                try {
                    // Télécharger le fichier via l'API avec authentification
                    const blob = await filesApi.downloadBlob(file.id);

                    // Créer une URL blob locale
                    const blobUrl = URL.createObjectURL(blob);
                    setImageBlobUrl(blobUrl);
                    setImageLoading(false);
                } catch (error) {
                    console.error('Erreur lors du chargement de l\'image:', error);
                    setImageError('Impossible de charger l\'image');
                    setImageLoading(false);
                }
            };

            loadImage();

            // Cleanup: révoquer l'URL blob lors du démontage
            return () => {
                if (imageBlobUrl) {
                    URL.revokeObjectURL(imageBlobUrl);
                }
            };
        }
    }, [isImage, file.id]);

    const clearScene = () => {
        if (sceneRef.current) {
            // Supprimer tous les objets de la scène sauf les lumières
            const objectsToRemove = [];
            sceneRef.current.traverse((child) => {
                if (child.isMesh || child.isGroup) {
                    objectsToRemove.push(child);
                }
            });
            objectsToRemove.forEach((obj) => {
                sceneRef.current.remove(obj);
                if (obj.geometry) obj.geometry.dispose();
                if (obj.material) {
                    if (Array.isArray(obj.material)) {
                        obj.material.forEach(mat => mat.dispose());
                    } else {
                        obj.material.dispose();
                    }
                }
            });
        }
        meshRef.current = null;
    };

    const loadSTLFile = async () => {
        // Éviter les appels multiples avec une ref
        if (loadingRef.current) {
            console.log('STL loading already in progress, skipping...');
            return;
        }
        
        loadingRef.current = true;
        console.log('Loading STL file for:', file);
        
        setStlLoading(true);
        setStlError(null);
        
        // Nettoyer complètement la scène
        clearScene();
        
        if (!file.id) {
            // Si pas d'ID de fichier, afficher une erreur
            console.log('No file ID found');
            setStlError('ID du fichier manquant');
            setStlLoading(false);
            loadingRef.current = false;
            return;
        }

        try {
            console.log('Downloading STL file with authentication for file ID:', file.id);
            
            // Télécharger le fichier avec authentification
            const blob = await filesApi.downloadBlob(file.id);
            console.log('File downloaded successfully, size:', blob.size);
            
            // Créer une URL locale à partir du blob
            const blobUrl = URL.createObjectURL(blob);
            console.log('Created blob URL:', blobUrl);
            
            const loader = new STLLoader();
            
            loader.load(
                blobUrl,
                (geometry) => {
                    // Nettoyer l'URL blob après utilisation
                    URL.revokeObjectURL(blobUrl);
                    
                    // Géométrie chargée avec succès
                    console.log('STL file loaded successfully:', geometry);
                    const material = new THREE.MeshLambertMaterial({ 
                        color: 0x888888,
                        transparent: false,
                        side: THREE.DoubleSide
                    });
                    
                    const mesh = new THREE.Mesh(geometry, material);
                    
                    // Centrer et redimensionner l'objet
                    const box = new THREE.Box3().setFromObject(mesh);
                    const center = box.getCenter(new THREE.Vector3());
                    const size = box.getSize(new THREE.Vector3());
                    
                    console.log('STL Bounding Box:', { center, size });
                    
                    // Centrer l'objet
                    mesh.position.sub(center);
                    
                    // Redimensionner pour que l'objet tienne dans la vue
                    const maxDim = Math.max(size.x, size.y, size.z);
                    const scale = maxDim > 0 ? 10 / maxDim : 1;
                    mesh.scale.setScalar(scale);
                    
                    console.log('STL scaling applied:', { maxDim, scale });
                    
                    // Cadrer la caméra autour de l'objet en utilisant son rayon englobant
                    // et la fov : distance = radius / sin(fov/2) avec une marge.
                    if (cameraRef.current) {
                        const sphere = new THREE.Sphere();
                        new THREE.Box3().setFromObject(mesh).getBoundingSphere(sphere);
                        const radius = sphere.radius;
                        const fov = (cameraRef.current.fov * Math.PI) / 180;
                        const distance = (radius / Math.sin(fov / 2)) * 1.2;

                        cameraRef.current.position.set(0, 0, distance);
                        cameraRef.current.near = Math.max(0.1, distance / 100);
                        cameraRef.current.far = distance * 100;
                        cameraRef.current.updateProjectionMatrix();
                        cameraRef.current.lookAt(0, 0, 0);

                        if (controlsRef.current) {
                            controlsRef.current.target.set(0, 0, 0);
                            controlsRef.current.minDistance = radius * 0.5;
                            controlsRef.current.maxDistance = radius * 50;
                            controlsRef.current.update();
                        }
                    }

                    meshRef.current = mesh;
                    sceneRef.current.add(mesh);
                    
                    setStlLoading(false);
                    loadingRef.current = false;
                },
                (progress) => {
                    // Progression du chargement
                    console.log('Chargement STL:', (progress.loaded / progress.total * 100) + '%');
                },
                (error) => {
                    // Nettoyer l'URL blob en cas d'erreur
                    URL.revokeObjectURL(blobUrl);
                    
                    // Erreur de chargement
                    console.error('Erreur lors du chargement du fichier STL:', error);
                    
                    setStlError('Erreur lors du chargement du fichier STL: ' + (error.message || 'Erreur inconnue'));
                    setStlLoading(false);
                    loadingRef.current = false;
                }
            );
        } catch (error) {
            console.error('Erreur lors du téléchargement du fichier STL:', error);
            setStlError('Erreur lors du téléchargement du fichier STL: ' + (error.message || 'Non autorisé'));
            setStlLoading(false);
            loadingRef.current = false;
        }
    };

    const createDemoSTLGeometry = () => {
        try {
            // Nettoyer la scène avant de créer la géométrie de démo  
            clearScene();
            
            // Création d'une géométrie de démo qui ressemble à une prothèse dentaire
            const group = new THREE.Group();

            // Base de la couronne (cylindre)
            const baseGeometry = new THREE.CylinderGeometry(0.8, 1.2, 1.5, 8);
            const baseMaterial = new THREE.MeshLambertMaterial({ 
                color: 0xf8f8ff,
                transparent: true,
                opacity: 0.9
            });
            const base = new THREE.Mesh(baseGeometry, baseMaterial);
            base.position.y = -0.75;
            group.add(base);

            // Partie supérieure (sphère tronquée)
            const topGeometry = new THREE.SphereGeometry(0.8, 8, 6, 0, Math.PI * 2, 0, Math.PI * 0.7);
            const topMaterial = new THREE.MeshLambertMaterial({ 
                color: 0xfffff0,
                transparent: true,
                opacity: 0.95
            });
            const top = new THREE.Mesh(topGeometry, topMaterial);
            top.position.y = 0.3;
            group.add(top);

            // Petits détails (bosses)
            for (let i = 0; i < 4; i++) {
                const detailGeometry = new THREE.SphereGeometry(0.15, 6, 6);
                const detailMaterial = new THREE.MeshLambertMaterial({ color: 0xffffff });
                const detail = new THREE.Mesh(detailGeometry, detailMaterial);
                const angle = (i / 4) * Math.PI * 2;
                detail.position.set(
                    Math.cos(angle) * 0.6,
                    0.5 + Math.random() * 0.3,
                    Math.sin(angle) * 0.6
                );
                group.add(detail);
            }

            meshRef.current = group;
            sceneRef.current.add(group);
            
            // Centrer l'objet
            const box = new THREE.Box3().setFromObject(group);
            const center = box.getCenter(new THREE.Vector3());
            group.position.sub(center);

            setStlLoading(false);
            setStlLoading(false);
            loadingRef.current = false;
        } catch (error) {
            console.error('Erreur lors de la création de la géométrie:', error);
            setStlError('Erreur lors du chargement du fichier STL');
            setStlLoading(false);
            setStlLoading(false);
            loadingRef.current = false;
        }
    };

    const animate = () => {
        if (!rendererRef.current || !sceneRef.current || !cameraRef.current) return;

        if (controlsRef.current) {
            controlsRef.current.update();
        }

        rendererRef.current.render(sceneRef.current, cameraRef.current);
        animationIdRef.current = requestAnimationFrame(animate);
    };

    // Gestion du zoom pour les images
    const handleZoomIn = () => {
        setImageScale(prev => Math.min(prev * 1.2, 5));
    };

    const handleZoomOut = () => {
        setImageScale(prev => Math.max(prev / 1.2, 0.1));
    };

    const handleResetView = () => {
        setImageScale(1);
        setImagePosition({ x: 0, y: 0 });
    };

    // Gestion du drag pour les images
    const handleMouseDown = (e) => {
        if (!isImage) return;
        setIsDragging(true);
        setDragStart({
            x: e.clientX - imagePosition.x,
            y: e.clientY - imagePosition.y
        });
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !isImage) return;
        setImagePosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Gestion du resize pour Three.js
    const handleResize = useCallback(() => {
        if (isSTL && canvasRef.current && rendererRef.current && cameraRef.current) {
            const width = canvasRef.current.clientWidth;
            const height = canvasRef.current.clientHeight;
            
            cameraRef.current.aspect = width / height;
            cameraRef.current.updateProjectionMatrix();
            rendererRef.current.setSize(width, height);
        }
    }, [isSTL]);

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isSTL, handleResize]);

    // Gestion de la fermeture animée
    const handleClose = useCallback(() => {
        if (isClosing) return;
        setIsClosing(true);
        setTimeout(() => {
            onClose();
        }, ANIMATION_DURATION);
    }, [isClosing, onClose]);

    // Gestion de la touche Échap pour fermer
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                handleClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleClose]);

    const handleDownload = async () => {
        if (!file.id) {
            console.log(`Impossible de télécharger le fichier: ${file.name} - ID manquant`);
            return;
        }

        try {
            console.log('Téléchargement du fichier avec authentification:', file.id);
            
            // Utiliser l'API authentifiée pour télécharger le fichier
            const blob = await filesApi.downloadBlob(file.id);
            
            // Créer une URL locale à partir du blob et déclencher le téléchargement
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = file.name || 'fichier';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Nettoyer l'URL blob après utilisation
            URL.revokeObjectURL(blobUrl);
            
            console.log('Fichier téléchargé avec succès');
        } catch (error) {
            console.error('Erreur lors du téléchargement du fichier:', error);
        }
    };

    return (
        <div
            className={`fixed inset-0 overlay-frost z-50 flex items-center justify-center ${isFullscreen ? '' : 'p-4'} ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
            onClick={(e) => {
                // Fermer si on clique en dehors du contenu
                if (e.target === e.currentTarget) {
                    handleClose();
                }
            }}
        >
            <div
                ref={containerRef}
                className={`glass-deep border border-white/10 rounded-lg overflow-hidden ${
                    isFullscreen ? 'w-full h-full' : 'max-w-4xl max-h-[90vh] w-full'
                } ${isClosing ? 'animate-modal-out' : 'animate-modal-in'}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/10 bg-black/30">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-white truncate">{file.name}</h3>
                        <span className="text-sm text-gray-400 hidden sm:block">
                            {file.size} • {isImage ? 'Image' : isSTL ? 'Modèle 3D' : 'Fichier'}
                        </span>
                    </div>

                    {/* Bouton de fermeture toujours visible */}
                    <button
                        onClick={handleClose}
                        className="ml-2 p-3 text-gray-400 hover:text-gray-200 hover:bg-white/15 rounded-full flex-shrink-0"
                        title="Fermer"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                {/* Barre d'outils séparée pour mobile */}
                <div className="flex items-center justify-center gap-1 p-2 bg-black/30 border-b border-white/10">
                    {isImage && (
                        <>
                            <button
                                onClick={handleZoomOut}
                                className="p-2 text-gray-400 hover:text-gray-200 hover:bg-white/15 rounded-full"
                                title="Dézoomer"
                            >
                                <ZoomOut className="h-5 w-5" />
                            </button>
                            <button
                                onClick={handleZoomIn}
                                className="p-2 text-gray-400 hover:text-gray-200 hover:bg-white/15 rounded-full"
                                title="Zoomer"
                            >
                                <ZoomIn className="h-5 w-5" />
                            </button>
                            <button
                                onClick={handleResetView}
                                className="p-2 text-gray-400 hover:text-gray-200 hover:bg-white/15 rounded-full"
                                title="Réinitialiser la vue"
                            >
                                <RotateCw className="h-5 w-5" />
                            </button>
                        </>
                    )}

                    <button
                        onClick={handleDownload}
                        className="p-2 text-gray-400 hover:text-gray-200 hover:bg-white/15 rounded-full"
                        title="Télécharger"
                    >
                        <Download className="h-5 w-5" />
                    </button>

                    <button
                        onClick={() => setIsFullscreen(!isFullscreen)}
                        className="p-2 text-gray-400 hover:text-gray-200 hover:bg-white/15 rounded-full"
                        title={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
                    >
                        <Maximize2 className="h-5 w-5" />
                    </button>
                </div>

                {/* Contenu */}
                <div className={`relative overflow-hidden ${isFullscreen ? 'h-full' : 'h-[500px]'}`}>
                    {isImage && (
                        <div
                            className="w-full h-full flex items-center justify-center cursor-move bg-black"
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                        >
                            {imageLoading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black">
                                    <div className="text-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600 mx-auto mb-3"></div>
                                        <p className="text-gray-300">Chargement de l'image...</p>
                                    </div>
                                </div>
                            )}

                            {imageError && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black">
                                    <div className="text-center text-red-400">
                                        <p className="text-lg mb-2">⚠️ Erreur de chargement</p>
                                        <p className="text-sm">{imageError}</p>
                                    </div>
                                </div>
                            )}

                            {imageBlobUrl && !imageLoading && !imageError && (
                                <>
                                    <img
                                        src={imageBlobUrl}
                                        alt={file.name}
                                        className="max-w-none pointer-events-none select-none"
                                        style={{
                                            transform: `scale(${imageScale}) translate(${imagePosition.x / imageScale}px, ${imagePosition.y / imageScale}px)`,
                                            transformOrigin: 'center center'
                                        }}
                                        onLoad={() => console.log('Image chargée')}
                                        onError={() => console.log('Erreur de chargement image')}
                                    />

                                    {/* Indicateur de zoom */}
                                    <div className="absolute bottom-4 left-4 overlay-frost text-white px-3 py-1 rounded text-sm">
                                        {Math.round(imageScale * 100)}%
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {isSTL && (
                        <div className="w-full h-full relative bg-black">
                            {stlLoading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black">
                                    <div className="text-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600 mx-auto mb-3"></div>
                                        <p className="text-gray-300">Chargement du modèle 3D...</p>
                                    </div>
                                </div>
                            )}

                            {stlError && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black">
                                    <div className="text-center text-red-400">
                                        <p className="text-lg mb-2">⚠️ Erreur de chargement</p>
                                        <p className="text-sm">{stlError}</p>
                                    </div>
                                </div>
                            )}

                            <canvas
                                ref={canvasRef}
                                className="w-full h-full"
                                style={{ display: stlLoading || stlError ? 'none' : 'block' }}
                            />

                            {/* Indicateur 3D */}
                            {!stlLoading && !stlError && (
                                <div className="absolute bottom-4 left-4 overlay-frost text-white px-3 py-2 rounded text-xs space-y-0.5">
                                    <p>🖱️ Glisser : pivoter</p>
                                    <p>Clic droit : déplacer</p>
                                    <p>Molette : zoom</p>
                                </div>
                            )}
                        </div>
                    )}

                    {!isImage && !isSTL && (
                        <div className="w-full h-full flex items-center justify-center bg-black">
                            <div className="text-center text-gray-400">
                                <div className="text-4xl mb-4">📄</div>
                                <p className="text-lg">Aperçu non disponible</p>
                                <p className="text-sm mb-4">Ce type de fichier ne peut pas être prévisualisé</p>
                                {file.id && (
                                    <button
                                        onClick={handleDownload}
                                        className="inline-flex items-center space-x-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors"
                                    >
                                        <Download className="h-4 w-4" />
                                        <span>Télécharger le fichier</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer avec informations */}
                <div className="p-3 bg-black/30 border-t border-white/10 text-sm text-gray-300">
                    {isImage && (
                        <p>💡 Utilisez la molette de la souris pour zoomer, et glissez pour déplacer l'image</p>
                    )}
                    {isSTL && (
                        <p>💡 Glissez pour pivoter, clic droit (ou Shift+glisser) pour déplacer, molette pour zoomer</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FilePreview;